/**
 * EVIDENCIAS DE OT / LÍNEA / NC — COMPONENTE ÚNICO (FASE 4.4-D)
 *
 * Un solo componente parametrizado por `ambito` ('OT'|'LINEA'|'NC') y por
 * `ruta` (el endpoint de listado/subida ya resuelto por quien lo integra:
 * `/api/ots/:id/evidencias`, `/api/ots/:id/lineas/:id/evidencias` o
 * `/api/no-conformidades/:id/evidencias`) — nunca tres implementaciones
 * distintas, y nunca acoplado a una pantalla concreta: quien lo usa decide
 * el contexto pasando la ruta, este componente no conoce OT ni NC.
 *
 * SIN PATCH GENÉRICO: subir es `POST ruta` (multipart), anular es
 * `POST /api/evidencias/:id/anular` y cambiar visibilidad es
 * `POST /api/evidencias/:id/visibilidad` — tres acciones con nombre propio,
 * igual que ya hace el backend (encargo, sección 2 y 4.4-C).
 *
 * PERMISOS: reflejan el contrato real, nunca lo amplían.
 * `evidencias:leer/subir/anular` deciden qué se pinta — igual que
 * `accionesPermitidas` en líneas de OT o `puedeAccionNc` en no
 * conformidades, el servidor revalida siempre y puede rechazar.
 *
 * `permitirSubidaForzada` es la ÚNICA excepción, y no es una ampliación de
 * permisos: es la capacidad que YA tiene CLIENTE (bypass de
 * `subirEvidenciaNcCliente`, sin `evidencias:subir`) sobre su propia NC de
 * origen CLIENTE — quien integra este componente en la ficha de una NC
 * decide si aplica (mirando `nc.origen` e `idCliente` de la sesión), nunca
 * este componente por su cuenta.
 */
import { useEffect, useId, useState, type ReactNode } from 'react';

import {
  api,
  descargarConSesion,
  CONTEXTOS_NC,
  CONTEXTOS_OT_LINEA,
  type AmbitoEvidencia,
  type ContextoEvidencia,
  type EvidenciaResumen,
  type VisibilidadEvidencia,
} from '../api.ts';
import { useSesion } from '../contexto.tsx';
import { Cargando, ConfirmDialog, Error as ErrorCaja, Modal, fechaHora, useDatos, Vacio } from './base.tsx';

const ETIQUETAS_CONTEXTO: Record<string, string> = {
  GENERAL: 'General',
  ANTES: 'Antes',
  DESPUES: 'Después',
  RESULTADO: 'Resultado',
  APERTURA_NC: 'Apertura',
  TRATAMIENTO_NC: 'Tratamiento',
  RESOLUCION_NC: 'Resolución',
};

/** Mismo límite que `LIMITE_EVIDENCIA_BYTES` (backend/src/almacenamiento/evidencias.ts) — sólo para no mandar de más; el backend sigue siendo quien decide de verdad. */
const LIMITE_BYTES = 10 * 1024 * 1024;

function formatoBytes(n: number): string {
  if (n < 1024) return `${String(n)} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function truncar(s: string, max: number): string {
  return s.length <= max ? s : `${s.slice(0, max - 1)}…`;
}

/** Fetch autenticado del binario — nunca una URL pública directa (encargo, sección 7/12). */
async function obtenerBlobEvidencia(id: string): Promise<Blob | null> {
  return descargarConSesion(`/api/evidencias/${id}/archivo`);
}

export interface EvidenciasProps {
  readonly ambito: AmbitoEvidencia;
  readonly ruta: string;
  readonly permitirSubidaForzada?: boolean;
  /**
   * FASE 7.2 — acceso CONTEXTUAL (responsable de una acción, sin
   * `evidencias:leer`): el backend decide si esta ruta le corresponde; aquí
   * sólo se deja de esconder la sección. Nunca anular ni cambiar visibilidad.
   */
  readonly accesoPropio?: boolean;
  readonly titulo?: string;
}

export function Evidencias({ ambito, ruta, permitirSubidaForzada = false, titulo = 'Evidencias', accesoPropio = false }: EvidenciasProps): ReactNode {
  const { puede } = useSesion();
  const puedeLeer = puede('evidencias:leer') || accesoPropio;
  const puedeSubirNormal = puede('evidencias:subir') && !accesoPropio;
  const puedeSubir = puedeSubirNormal || permitirSubidaForzada || accesoPropio;
  //  FASE 7.2: la evidencia de una acción correctiva es SIEMPRE interna (el
  //  servidor la fuerza): ni selector al subir ni cambio de visibilidad
  //  después — ofrecerlos sería engañoso.
  const siempreInterna = ambito === 'ACCION';
  //  Mismo permiso que exige el backend en `POST /api/evidencias/:id/visibilidad`
  //  (FASE 4.4-D) — nunca uno inventado aquí. F11.3: la de una acción también se
  //  comparte (nace interna); el cliente sólo la ve si además la acción y la NC lo son.
  const puedeCambiarVisibilidad = puedeSubirNormal;
  const puedeAnular = puede('evidencias:anular') && !accesoPropio;

  const [incluirAnuladas, setIncluirAnuladas] = useState(false);
  const [subiendo, setSubiendo] = useState(false);
  const [mensaje, setMensaje] = useState<string | null>(null);

  const estado = useDatos(
    () =>
      puedeLeer
        ? api.get<{ filas: EvidenciaResumen[] }>(`${ruta}${incluirAnuladas ? '?incluirAnuladas=true' : ''}`)
        : Promise.resolve({ filas: [] }),
    [ruta, incluirAnuladas, puedeLeer],
  );

  if (!puedeLeer) return null;

  //  FASE 7.1: la acción correctiva usa el vocabulario de su NC; la incidencia, el de OT/línea.
  const contextos: readonly ContextoEvidencia[] = ambito === 'NC' || ambito === 'ACCION' ? CONTEXTOS_NC : CONTEXTOS_OT_LINEA;

  function alCambiar(): void {
    estado.recargar();
  }

  /**
   * Tras anular: el listado actual usaba `incluirAnuladas=false` (el
   * backend por defecto sólo devuelve ACTIVA), así que un simple
   * `recargar()` haría desaparecer justo la fila que se acaba de anular —
   * lo contrario de "permanece en el histórico" (encargo, sección 8).
   * Cambiar `incluirAnuladas` es dependencia de `useDatos`: ya dispara el
   * refetch por sí solo, sin necesidad de `recargar()` además.
   */
  function alAnular(): void {
    setIncluirAnuladas(true);
  }

  return (
    <div className="panel evidencias-seccion">
      <div className="panel-cabecera">
        <h3>{titulo}</h3>
        <div className="fila" style={{ gap: 10, flexWrap: 'wrap' }}>
          <label className="fila pequena suave" style={{ gap: 4 }}>
            <input
              type="checkbox"
              checked={incluirAnuladas}
              onChange={(e) => setIncluirAnuladas(e.target.checked)}
            />
            Ver anuladas
          </label>
          {puedeSubir && !subiendo && (
            <button type="button" className="pequeno" onClick={() => { setSubiendo(true); setMensaje(null); }}>
              Adjuntar evidencia
            </button>
          )}
        </div>
      </div>
      <div className="panel-cuerpo">
        {mensaje !== null && !subiendo && (
          <div className="nota exito" role="status">
            {mensaje}
          </div>
        )}
        {subiendo && (
          <FormularioSubidaEvidencia
            ruta={ruta}
            contextos={contextos}
            forzada={!puedeSubirNormal || siempreInterna}
            notaInterna={siempreInterna}
            cancelar={() => setSubiendo(false)}
            alSubir={() => {
              setSubiendo(false);
              setMensaje('Evidencia adjuntada.');
              alCambiar();
            }}
          />
        )}
        {estado.cargando && estado.datos === null && <Cargando texto="Cargando evidencias…" />}
        {estado.error !== null && <ErrorCaja error={estado.error} reintentar={estado.recargar} />}
        {estado.datos !== null &&
          (estado.datos.filas.length === 0 ? (
            <Vacio titulo="No hay evidencias adjuntas" />
          ) : (
            <ul className="lista-evidencias">
              {estado.datos.filas.map((f) => (
                <TarjetaEvidencia
                  key={f.id}
                  evidencia={f}
                  puedeCambiarVisibilidad={puedeCambiarVisibilidad}
                  puedeAnular={puedeAnular}
                  onCambiada={alCambiar}
                  onAnulada={alAnular}
                />
              ))}
            </ul>
          ))}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ subida

function FormularioSubidaEvidencia({
  ruta,
  contextos,
  forzada,
  notaInterna = false,
  cancelar,
  alSubir,
}: {
  notaInterna?: boolean;
  ruta: string;
  contextos: readonly ContextoEvidencia[];
  /** CLIENTE vía bypass: sin selector de visibilidad, el servidor la fuerza a CLIENTE. */
  forzada: boolean;
  cancelar: () => void;
  alSubir: () => void;
}): ReactNode {
  const idBase = useId();
  const [contexto, setContexto] = useState<ContextoEvidencia>(contextos[0] ?? 'GENERAL');
  const [descripcion, setDescripcion] = useState('');
  const [tomadaEn, setTomadaEn] = useState('');
  const [visibilidad, setVisibilidad] = useState<VisibilidadEvidencia>('INTERNA');
  const [archivo, setArchivo] = useState<File | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<unknown>(null);

  function alElegirArchivo(lista: FileList | null): void {
    const f = lista?.[0];
    if (f === undefined) {
      setArchivo(null);
      return;
    }
    //  Comprobación de tamaño ANTES de subir — evita un viaje de red inútil
    //  (encargo, sección 5/11: "error de tamaño" es un estado propio de la
    //  UI). El backend repite exactamente esta comprobación: es sólo una
    //  conveniencia, nunca la autoridad.
    if (f.size > LIMITE_BYTES) {
      setError(new globalThis.Error(`«${f.name}» pesa ${formatoBytes(f.size)}: el máximo permitido es 10 MB.`));
      setArchivo(null);
      return;
    }
    setError(null);
    setArchivo(f);
  }

  async function enviar(): Promise<void> {
    if (archivo === null) {
      setError(new globalThis.Error('Elige un fichero para adjuntar.'));
      return;
    }
    setEnviando(true);
    setError(null);
    try {
      const datos = new FormData();
      datos.set('contexto', contexto);
      if (descripcion.trim() !== '') datos.set('descripcion', descripcion.trim());
      if (tomadaEn.trim() !== '') {
        const d = new Date(tomadaEn);
        if (!Number.isNaN(d.getTime())) datos.set('tomadaEn', d.toISOString());
      }
      if (!forzada) datos.set('visibilidad', visibilidad);
      datos.set('archivo', archivo, archivo.name);
      await api.subir(ruta, datos);
      alSubir();
    } catch (e) {
      setError(e);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="panel accion-linea evidencia-formulario">
      <div className="panel-cabecera">
        <h4>Adjuntar evidencia</h4>
        <button type="button" className="pequeno" onClick={cancelar} disabled={enviando}>
          Cancelar
        </button>
      </div>
      <div className="panel-cuerpo">
        {error !== null && <ErrorCaja error={error} />}
        <div className="campo">
          <label htmlFor={`${idBase}-archivo`}>Fichero</label>
          <input
            id={`${idBase}-archivo`}
            type="file"
            accept="image/*,application/pdf"
            onChange={(e) => alElegirArchivo(e.target.files)}
          />
          {archivo !== null && (
            <p className="pequena suave">
              {truncar(archivo.name, 60)} · {archivo.type !== '' ? archivo.type : 'tipo desconocido'} ·{' '}
              {formatoBytes(archivo.size)}
            </p>
          )}
        </div>
        <div className="fila" style={{ gap: 12, flexWrap: 'wrap' }}>
          <div className="campo" style={{ margin: 0 }}>
            <label htmlFor={`${idBase}-contexto`}>Momento</label>
            <select
              id={`${idBase}-contexto`}
              value={contexto}
              onChange={(e) => setContexto(e.target.value as ContextoEvidencia)}
            >
              {contextos.map((c) => (
                <option key={c} value={c}>
                  {ETIQUETAS_CONTEXTO[c] ?? c}
                </option>
              ))}
            </select>
          </div>
          {notaInterna && (
            <p className="pequena suave" style={{ margin: 0, alignSelf: 'flex-end' }}>
              Se sube como interna; puedes compartirla después.
            </p>
          )}
          {!forzada && (
            <div className="campo" style={{ margin: 0 }}>
              <label htmlFor={`${idBase}-vis`}>Visibilidad</label>
              <select
                id={`${idBase}-vis`}
                value={visibilidad}
                onChange={(e) => setVisibilidad(e.target.value as VisibilidadEvidencia)}
              >
                <option value="INTERNA">Solo interna</option>
                <option value="CLIENTE">Compartir con cliente</option>
              </select>
            </div>
          )}
        </div>
        <div className="campo">
          <label htmlFor={`${idBase}-desc`}>Descripción (opcional)</label>
          <textarea id={`${idBase}-desc`} value={descripcion} onChange={(e) => setDescripcion(e.target.value)} rows={2} />
        </div>
        <div className="campo">
          <label htmlFor={`${idBase}-tomada`}>Tomada el (opcional)</label>
          <input
            id={`${idBase}-tomada`}
            type="datetime-local"
            value={tomadaEn}
            onChange={(e) => setTomadaEn(e.target.value)}
          />
        </div>
        <button type="button" className="principal" disabled={enviando} onClick={() => void enviar()}>
          {enviando ? 'Subiendo…' : 'Subir evidencia'}
        </button>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------- listado

function EstadoEvidencia({ estado }: { estado: string }): ReactNode {
  return (
    <span className={`etiqueta ${estado === 'ACTIVA' ? 'exito' : 'error'}`}>
      {estado === 'ACTIVA' ? 'Activa' : 'Anulada'}
    </span>
  );
}

/**
 * Miniatura de imagen (lectura perezosa, blob autenticado — mismo patrón
 * que `componentes/logo-remoto.ts`) o icono de PDF. Revoca su URL de objeto
 * al desmontar o al cambiar de evidencia (encargo, sección 12): nunca deja
 * un blob vivo más de lo necesario.
 */
function MiniaturaEvidencia({ id, mime, nombreOriginal }: { id: string; mime: string; nombreOriginal: string }): ReactNode {
  const esImagen = mime.startsWith('image/');
  const [url, setUrl] = useState<string | null>(null);
  const [fallo, setFallo] = useState(false);

  useEffect(() => {
    if (!esImagen) return;
    let urlCreada: string | null = null;
    let vivo = true;
    void obtenerBlobEvidencia(id).then((blob) => {
      if (!vivo) return;
      if (blob === null) {
        setFallo(true);
        return;
      }
      urlCreada = URL.createObjectURL(blob);
      setUrl(urlCreada);
    });
    return () => {
      vivo = false;
      if (urlCreada !== null) URL.revokeObjectURL(urlCreada);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sólo depende de la identidad de la evidencia
  }, [id, esImagen]);

  if (!esImagen) {
    return (
      <div className="evidencia-miniatura evidencia-miniatura-pdf" aria-hidden="true">
        PDF
      </div>
    );
  }
  if (fallo) {
    return (
      <div className="evidencia-miniatura evidencia-miniatura-vacia" aria-hidden="true">
        —
      </div>
    );
  }
  if (url === null) {
    return <div className="evidencia-miniatura evidencia-miniatura-vacia" aria-hidden="true" />;
  }
  return <img className="evidencia-miniatura" src={url} alt={nombreOriginal} />;
}

function BotonAbrirEvidencia({ id, nombreOriginal }: { id: string; nombreOriginal: string }): ReactNode {
  const [cargando, setCargando] = useState(false);
  const [fallo, setFallo] = useState(false);

  async function abrir(): Promise<void> {
    setCargando(true);
    setFallo(false);
    const blob = await obtenerBlobEvidencia(id);
    setCargando(false);
    if (blob === null) {
      setFallo(true);
      return;
    }
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank', 'noopener');
    //  La pestaña nueva necesita la URL viva al abrir el fichero; no hay
    //  forma fiable de saber desde aquí cuándo ha terminado de leerla, así
    //  que se revoca con margen en vez de al instante (encargo, sección 12:
    //  no mantener el blob vivo indefinidamente, pero tampoco romper la
    //  apertura).
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  }

  return (
    <span className="fila" style={{ gap: 6 }}>
      <button type="button" className="pequeno" disabled={cargando} onClick={() => void abrir()} aria-label={`Abrir ${nombreOriginal}`}>
        {cargando ? 'Abriendo…' : 'Abrir'}
      </button>
      {fallo && (
        <span className="pequena tenue" role="alert">
          No se ha podido abrir.
        </span>
      )}
    </span>
  );
}

/**
 * F11.1 — compartir o retirar una evidencia es un cambio sensible: siempre se
 * confirma, y con un vocabulario distinto del de la NC entera («Compartir con
 * cliente» / «Solo interna», nunca «Hacer visible al cliente»).
 */
function ControlVisibilidad({ evidencia, onCambiada }: { evidencia: EvidenciaResumen; onCambiada: () => void }): ReactNode {
  const [enviando, setEnviando] = useState(false);
  const [confirmando, setConfirmando] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const destino: VisibilidadEvidencia = evidencia.visibilidad === 'CLIENTE' ? 'INTERNA' : 'CLIENTE';

  async function cambiar(): Promise<void> {
    setEnviando(true);
    setError(null);
    try {
      await api.post(`/api/evidencias/${evidencia.id}/visibilidad`, { visibilidad: destino });
      setConfirmando(false);
      onCambiada();
    } catch (e) {
      setConfirmando(false);
      setError(e);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <span className="fila" style={{ gap: 6, flexWrap: 'wrap' }}>
      <button type="button" className="pequeno" disabled={enviando} onClick={() => setConfirmando(true)}>
        {destino === 'CLIENTE' ? 'Compartir con cliente' : 'Dejar solo interna'}
      </button>
      {confirmando && (
        <ConfirmDialog
          titulo={destino === 'CLIENTE' ? '¿Compartir esta evidencia con el cliente?' : '¿Dejar esta evidencia solo interna?'}
          descripcion={destino === 'CLIENTE'
            ? `El cliente podrá ver y descargar «${evidencia.nombreOriginal}» si tiene acceso a lo que documenta.`
            : `El cliente dejará de ver «${evidencia.nombreOriginal}».`}
          confirmar={destino === 'CLIENTE' ? 'Compartir con cliente' : 'Dejar solo interna'}
          variante={destino === 'CLIENTE' ? 'principal' : 'peligro'}
          ocupado={enviando}
          alConfirmar={() => cambiar()}
          cerrar={() => setConfirmando(false)}
        />
      )}
      {error !== null && <ErrorCaja error={error} />}
    </span>
  );
}

function FormularioAnularEvidencia({
  id,
  cancelar,
  alAnular,
}: {
  id: string;
  cancelar: () => void;
  alAnular: () => void;
}): ReactNode {
  const idBase = useId();
  const [motivo, setMotivo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<unknown>(null);

  async function enviar(): Promise<void> {
    if (motivo.trim() === '') {
      setError(new globalThis.Error('El motivo de anulación es obligatorio.'));
      return;
    }
    setEnviando(true);
    setError(null);
    try {
      await api.post(`/api/evidencias/${id}/anular`, { motivo: motivo.trim() });
      alAnular();
    } catch (e) {
      setError(e);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Modal titulo="Anular evidencia" cerrar={cancelar}>
      <p className="pequena">
        La evidencia permanecerá en el histórico marcada como <strong>ANULADA</strong>: el fichero y la fila no se
        eliminan, y no existe forma de restaurarla después.
      </p>
      {error !== null && <ErrorCaja error={error} />}
      <div className="campo">
        <label htmlFor={`${idBase}-motivo`}>Motivo</label>
        <textarea id={`${idBase}-motivo`} value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={2} />
      </div>
      <button type="button" className="peligro" disabled={enviando} onClick={() => void enviar()}>
        {enviando ? 'Anulando…' : 'Anular evidencia'}
      </button>
    </Modal>
  );
}

function TarjetaEvidencia({
  evidencia,
  puedeCambiarVisibilidad,
  puedeAnular,
  onCambiada,
  onAnulada,
}: {
  evidencia: EvidenciaResumen;
  puedeCambiarVisibilidad: boolean;
  puedeAnular: boolean;
  onCambiada: () => void;
  onAnulada: () => void;
}): ReactNode {
  const [anulando, setAnulando] = useState(false);
  const esActiva = evidencia.estado === 'ACTIVA';

  return (
    <li className={`evidencia-tarjeta${esActiva ? '' : ' evidencia-anulada'}`}>
      <div className="evidencia-cuerpo">
        <MiniaturaEvidencia id={evidencia.id} mime={evidencia.mime} nombreOriginal={evidencia.nombreOriginal} />
        <div className="evidencia-datos">
          <div className="fila" style={{ gap: 6, flexWrap: 'wrap' }}>
            <span className="etiqueta neutra">{evidencia.mime.startsWith('image/') ? 'Imagen' : 'PDF'}</span>
            <span className="etiqueta info">{ETIQUETAS_CONTEXTO[evidencia.contexto] ?? evidencia.contexto}</span>
            <EstadoEvidencia estado={evidencia.estado} />
            <span className={`etiqueta ${evidencia.visibilidad === 'CLIENTE' ? 'info' : 'neutra'}`}>
              {evidencia.visibilidad === 'CLIENTE' ? 'Compartida con cliente' : 'Solo interna'}
            </span>
          </div>
          <div className="evidencia-nombre" title={evidencia.nombreOriginal}>
            {truncar(evidencia.nombreOriginal, 48)}
          </div>
          {evidencia.descripcion !== null && evidencia.descripcion !== '' && (
            <p className="pequena">{evidencia.descripcion}</p>
          )}
          <div className="pequena tenue">
            Subida {fechaHora(evidencia.subidaEn)}
            {evidencia.tomadaEn !== null && ` · Tomada ${fechaHora(evidencia.tomadaEn)}`}
          </div>
          {!esActiva && (
            <div className="pequena tenue">
              Anulada{evidencia.anuladoEn !== null && ` · ${fechaHora(evidencia.anuladoEn)}`}
              {evidencia.motivoAnulacion !== null && evidencia.motivoAnulacion !== '' && ` · ${evidencia.motivoAnulacion}`}
            </div>
          )}
        </div>
      </div>
      <div className="evidencia-acciones">
        <BotonAbrirEvidencia id={evidencia.id} nombreOriginal={evidencia.nombreOriginal} />
        {esActiva && puedeCambiarVisibilidad && <ControlVisibilidad evidencia={evidencia} onCambiada={onCambiada} />}
        {esActiva && puedeAnular && (
          <button type="button" className="pequeno peligro" onClick={() => setAnulando(true)}>
            Anular evidencia
          </button>
        )}
      </div>
      {anulando && (
        <FormularioAnularEvidencia
          id={evidencia.id}
          cancelar={() => setAnulando(false)}
          alAnular={() => {
            setAnulando(false);
            onAnulada();
          }}
        />
      )}
    </li>
  );
}

// ------------------------------------------------------- FASE 7.2 · propias

/**
 * Lo que el propio usuario subió (p.ej. la operaria a SU incidencia), sin
 * `evidencias:leer`: sólo lectura — miniatura y «Abrir». El backend sólo
 * devuelve y sirve sus propias evidencias (`/api/mis-incidencias/:id/evidencias`).
 */
export function EvidenciasPropias({ ruta }: { ruta: string }): ReactNode {
  const estado = useDatos(() => api.get<{ filas: EvidenciaResumen[] }>(ruta), [ruta]);
  if (estado.cargando && estado.datos === null) return <Cargando texto="Cargando fotos…" />;
  if (estado.error !== null) return <ErrorCaja error={estado.error} reintentar={estado.recargar} />;
  if (estado.datos === null || estado.datos.filas.length === 0) return <p className="pequena tenue">No has adjuntado fotos.</p>;
  return (
    <ul className="lista-evidencias" aria-label="Mis fotos">
      {estado.datos.filas.map((f) => (
        <li key={f.id} className="evidencia-tarjeta">
          {f.mime.startsWith('image/') && <MiniaturaEvidencia id={f.id} mime={f.mime} nombreOriginal={f.nombreOriginal} />}
          <div className="evidencia-cuerpo">
            <span className="pequena">{f.nombreOriginal}</span>
            <BotonAbrirEvidencia id={f.id} nombreOriginal={f.nombreOriginal} />
          </div>
        </li>
      ))}
    </ul>
  );
}

/**
 * FASE 7.3 — evidencias ya resueltas por el servidor para el PORTAL (DTO
 * público, sólo CLIENTE y de padre visible): miniatura y «Abrir», nada más.
 * La descarga vuelve a pasar por la RLS del portal en el servidor.
 */
export function ListaEvidenciasPublicas({ evidencias }: {
  evidencias: readonly { id: string; nombreOriginal: string; mime: string; descripcion: string | null; contexto?: string; subidaEn?: string }[];
}): ReactNode {
  if (evidencias.length === 0) return <p className="pequena tenue">No hay evidencias publicadas.</p>;
  return (
    <ul className="lista-evidencias" aria-label="Evidencias">
      {evidencias.map((f) => (
        <li key={f.id} className="evidencia-tarjeta">
          {f.mime.startsWith('image/') && <MiniaturaEvidencia id={f.id} mime={f.mime} nombreOriginal={f.nombreOriginal} />}
          <div className="evidencia-cuerpo">
            <span className="pequena">{f.nombreOriginal}</span>
            {f.descripcion !== null && <span className="pequena suave">{f.descripcion}</span>}
            {/*  F11.3: contexto y fecha legibles (nunca ISO crudo). */}
            {(f.contexto !== undefined || f.subidaEn !== undefined) && (
              <span className="pequena tenue">
                {[f.contexto === undefined ? null : ETIQUETAS_CONTEXTO[f.contexto] ?? f.contexto, f.subidaEn === undefined ? null : `Subida ${fechaHora(f.subidaEn)}`]
                  .filter((x) => x !== null).join(' · ')}
              </span>
            )}
            <BotonAbrirEvidencia id={f.id} nombreOriginal={f.nombreOriginal} />
          </div>
        </li>
      ))}
    </ul>
  );
}
